import React, { useEffect, useMemo, useState, useCallback } from "react";
import PublicLayout from "../components/PublicLayout";
import { api, formatError } from "../lib/api";
import { log } from "../lib/log";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import { toast } from "sonner";
import {
  Download, BookOpen, Heart, Users, Repeat, XCircle, Share2, MessageCircle,
  Link as LinkIcon, Smartphone, ImageIcon, Film, Truck, CheckCircle2, X,
  CalendarClock, CalendarDays, Clock, FileEdit, Edit3,
} from "lucide-react";

// Donor dashboard follows the user's language preference (English default).
// Schools and Admin dashboards remain in Kannada.
export default function DonorDashboard() {
  const { user, loading } = useAuth();
  const { t } = useT();
  const [donations, setDonations] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [proofGroups, setProofGroups] = useState([]);
  const [impact, setImpact] = useState({ total_books_funded: 0, total_amount: 0, total_schools: 0 });
  const [tab, setTab] = useState("donations");
  const [shareOpen, setShareOpen] = useState(false);

  // Scheduling state
  const [distPref, setDistPref] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [supportedSchools, setSupportedSchools] = useState([]);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);

  const load = useCallback(() => {
    const warn = (label) => (err) => log.warn(`[donor] ${label} load failed:`, err?.message || err);
    api.get("/donor/donations").then(r => {
      setDonations(r.data);
      // Build unique supported schools from SUCCESS donations
      const seen = new Map();
      r.data.filter(d => d.payment_status === "SUCCESS").forEach(d => {
        if (!seen.has(d.school_id)) seen.set(d.school_id, { school_id: d.school_id, name: d.school_name });
      });
      setSupportedSchools(Array.from(seen.values()));
    }).catch(warn("donations"));
    api.get("/donor/impact").then(r => setImpact(r.data)).catch(warn("impact"));
    api.get("/donor/pledges").then(r => setPledges(r.data)).catch(warn("pledges"));
    api.get("/donor/proofs").then(r => setProofGroups(r.data)).catch(warn("proofs"));
    api.get("/donor/distribution-preference").then(r => setDistPref(!!r.data.is_distribution_interest)).catch(warn("pref"));
    api.get("/donor/schedules").then(r => setSchedules(r.data)).catch(warn("schedules"));
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const cancelPledge = async (id) => {
    if (!confirm("Cancel this monthly pledge?")) return;
    try { await api.post(`/donor/pledges/${id}/cancel`); toast.success("Pledge cancelled"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const togglePref = async (val) => {
    setDistPref(val);
    try { await api.put("/donor/distribution-preference", { is_distribution_interest: val }); }
    catch (e) { setDistPref(!val); toast.error(formatError(e)); }
  };

  const cancelSchedule = async (sid) => {
    if (!confirm(t("visits_cancel_confirm"))) return;
    try {
      await api.put(`/donor/schedules/${sid}`, { status: "cancelled" });
      toast.success(t("visits_toast_cancelled"));
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  if (loading) return <PublicLayout><div className="p-20 text-center">Loading…</div></PublicLayout>;
  if (!user) return <PublicLayout><div className="p-20 text-center">Please <a className="bb-link" href="/login">sign in</a>.</div></PublicLayout>;

  const fileBase = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-14">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="bb-overline text-[var(--bb-terracotta)] mb-3">DONOR DASHBOARD</div>
            <h1 className="font-serif text-5xl md:text-6xl text-[var(--bb-forest)] tracking-tight leading-tight">Hello, {user.name?.split(" ")[0] || "friend"}.</h1>
            <p className="mt-4 text-[var(--bb-ink)]/75 max-w-xl">Your sponsorships, certificates, and impact updates from the schools you supported.</p>
          </div>
          <button
            onClick={() => setShareOpen(true)}
            className="bb-btn-primary inline-flex items-center gap-2"
            data-testid="donor-share-btn"
          >
            <Share2 size={14}/> {t("share_impact")}
          </button>
        </div>

        <div className="mt-10 grid sm:grid-cols-3 gap-6">
          <Tile icon={<BookOpen size={18}/>} label="Books sponsored" value={impact.total_books_funded} testid="tile-books"/>
          <Tile icon={<Users size={18}/>} label="Schools supported" value={impact.total_schools} testid="tile-schools"/>
          <Tile icon={<Heart size={18}/>} label="Total given" value={`₹${(impact.total_amount||0).toLocaleString()}`} testid="tile-total"/>
        </div>

        <div className="mt-12 flex gap-1 border-b border-[var(--bb-border)] overflow-x-auto">
          {[
            ["donations","Sponsorships"],
            ["impact","Impact Updates"],
            ["visits", t("visits_tab")],
            ["pledges", t("pledges_tab")],
          ].map(([k,l])=>(
            <button
              key={k}
              onClick={()=>setTab(k)}
              className={`px-5 py-3 text-sm whitespace-nowrap ${tab===k?"border-b-2 border-[var(--bb-terracotta)] text-[var(--bb-terracotta)]":"text-[var(--bb-muted)]"}`}
              data-testid={`donor-tab-${k}`}
            >
              {l}
              {k==="pledges" && pledges.filter(p=>p.status==="active").length>0 && ` (${pledges.filter(p=>p.status==="active").length})`}
              {k==="impact" && proofGroups.reduce((n,g)=>n+g.proofs.length,0) > 0 && ` (${proofGroups.reduce((n,g)=>n+g.proofs.length,0)})`}
              {k==="visits" && schedules.filter(s=>s.status!=="cancelled" && s.status!=="completed").length>0 && ` (${schedules.filter(s=>s.status!=="cancelled" && s.status!=="completed").length})`}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "donations" && (
            donations.length === 0 ? (
              <div className="bb-card p-12 text-center text-[var(--bb-muted)]">You haven't sponsored a school yet. <a className="bb-link" href="/schools">Browse schools</a>.</div>
            ) : (
              <div className="bb-card overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--bb-sand)] text-[var(--bb-muted)] bb-overline">
                    <tr>
                      <th className="text-left p-4">Date</th>
                      <th className="text-left p-4">School</th>
                      <th className="text-right p-4">Sets</th>
                      <th className="text-right p-4">Amount</th>
                      <th className="text-left p-4">Status</th>
                      <th className="p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {donations.map((d) => (
                      <tr key={d.donation_id} className="border-t border-[var(--bb-border)]" data-testid={`donation-row-${d.donation_id}`}>
                        <td className="p-4">{d.donation_date?.slice(0,10)}</td>
                        <td className="p-4">{d.school_name}</td>
                        <td className="p-4 text-right">{d.quantity}</td>
                        <td className="p-4 text-right">₹{d.total_amount?.toLocaleString()}</td>
                        <td className="p-4"><span className={`bb-tag ${d.payment_status==="SUCCESS"?"bb-tag-green":""}`}>{d.payment_status}</span></td>
                        <td className="p-4 text-right">
                          {d.payment_status === "SUCCESS" && (
                            <a target="_blank" rel="noreferrer" href={`${process.env.REACT_APP_BACKEND_URL}/api/donations/${d.donation_id}/certificate`} className="bb-link inline-flex items-center gap-1" data-testid={`cert-${d.donation_id}`}>
                              <Download size={14}/> {t("certificate")}
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {tab === "impact" && (
            <ImpactUpdates groups={proofGroups} t={t} fileBase={fileBase}/>
          )}

          {tab === "visits" && (
            <VisitsPanel
              t={t}
              distPref={distPref}
              onToggle={togglePref}
              supportedSchools={supportedSchools}
              schedules={schedules}
              onBooked={() => { load(); toast.success(t("visits_toast_scheduled")); }}
              onReschedule={(s) => setRescheduleTarget(s)}
              onCancel={cancelSchedule}
            />
          )}

          {tab === "pledges" && (
            pledges.length === 0 ? (
              <div className="bb-card p-12 text-center text-[var(--bb-muted)]">No monthly pledges yet. Set one up from any school page.</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-5">
                {pledges.map(p => (
                  <div key={p.pledge_id} className="bb-card p-6" data-testid={`pledge-${p.pledge_id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="bb-tag"><Repeat size={12}/> {p.status}</div>
                      <div className="text-xs text-[var(--bb-muted)]">{p.frequency}</div>
                    </div>
                    <div className="font-serif text-2xl text-[var(--bb-forest)]">{p.school_name}</div>
                    <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                      <div><div className="bb-overline text-[var(--bb-muted)]">Per month</div><div className="font-medium">₹{p.amount_per_charge?.toLocaleString()}</div></div>
                      <div><div className="bb-overline text-[var(--bb-muted)]">Charges</div><div className="font-medium">{p.charges_count || 0}</div></div>
                      <div><div className="bb-overline text-[var(--bb-muted)]">Next</div><div className="font-medium">{p.next_charge_at?.slice(0,10)}</div></div>
                    </div>
                    {p.status === "active" && (
                      <button onClick={() => cancelPledge(p.pledge_id)} className="mt-5 text-sm text-[var(--bb-error)] hover:underline flex items-center gap-1" data-testid={`pledge-cancel-${p.pledge_id}`}><XCircle size={14}/> Cancel pledge</button>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </section>

      {shareOpen && <ShareModal onClose={() => setShareOpen(false)} t={t} />}
      {rescheduleTarget && (
        <RescheduleModal
          target={rescheduleTarget}
          t={t}
          onClose={() => setRescheduleTarget(null)}
          onSaved={() => { setRescheduleTarget(null); load(); toast.success(t("visits_toast_rescheduled")); }}
        />
      )}
    </PublicLayout>
  );
}

function Tile({ icon, label, value, testid }) {
  return (
    <div className="bb-card p-6" data-testid={testid}>
      <div className="w-9 h-9 rounded-full bg-[var(--bb-terracotta)]/10 text-[var(--bb-terracotta)] grid place-items-center mb-4">{icon}</div>
      <div className="bb-overline text-[var(--bb-muted)]">{label}</div>
      <div className="font-serif text-4xl text-[var(--bb-forest)] mt-2">{value}</div>
    </div>
  );
}

function ImpactUpdates({ groups, t, fileBase }) {
  if (!groups || groups.length === 0) {
    return (
      <div className="bb-card p-12 text-center text-[var(--bb-muted)]" data-testid="impact-empty">
        {t("impact_updates_empty")}
      </div>
    );
  }
  return (
    <div className="space-y-10" data-testid="impact-updates">
      <div>
        <h2 className="font-serif text-3xl text-[var(--bb-forest)]">{t("impact_updates_title")}</h2>
        <p className="text-sm text-[var(--bb-muted)] mt-1">{t("impact_updates_sub")}</p>
      </div>
      {groups.map((g) => (
        <div key={g.school_id} data-testid={`impact-group-${g.school_id}`}>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="font-serif text-2xl text-[var(--bb-forest)]">{g.school_name}</div>
            <div className={`bb-tag inline-flex items-center gap-1 ${g.delivery_status === "delivered" ? "bb-tag-green" : ""}`}>
              {g.delivery_status === "delivered" ? <CheckCircle2 size={12}/> : <Truck size={12}/>}
              {g.delivery_status === "delivered" ? t("impact_delivery_confirmed") : t("impact_delivery_pending")}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            {g.proofs.map((p) => (
              <div key={p.proof_id} className="bb-card p-3" data-testid={`proof-${p.proof_id}`}>
                <div className="aspect-video bg-[var(--bb-sand)] rounded overflow-hidden mb-2">
                  {p.type === "image" ? (
                    <img src={fileBase + p.storage_path} alt="" className="w-full h-full object-cover"/>
                  ) : (
                    <div className="w-full h-full grid place-items-center text-[var(--bb-muted)]"><Film size={30}/></div>
                  )}
                </div>
                {p.caption && <div className="text-xs text-[var(--bb-ink)]/80 line-clamp-2 mb-1">{p.caption}</div>}
                <div className="text-[10px] text-[var(--bb-muted)] flex items-center gap-1">
                  <ImageIcon size={10}/> {p.created_at?.slice(0,10)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Distribution scheduling ----
const TIME_SLOTS = [
  "10:00", "10:30", "11:00", "11:30", "12:00",
  "12:30", "13:00", "13:30", "14:00", "14:30", "15:00",
];

function formatTimeLabel(t24) {
  if (!t24) return "";
  const [h, m] = t24.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}

function todayISO() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isWeekdayISO(iso) {
  if (!iso) return false;
  const d = new Date(iso + "T00:00:00");
  const dow = d.getDay(); // 0=Sun..6=Sat
  return dow !== 0; // allow Mon–Sat
}

function VisitsPanel({ t, distPref, onToggle, supportedSchools, schedules, onBooked, onReschedule, onCancel }) {
  const upcoming = useMemo(
    () => schedules.filter(s => s.status === "scheduled" || s.status === "rescheduled"),
    [schedules],
  );
  const history = useMemo(
    () => schedules.filter(s => s.status === "cancelled" || s.status === "completed"),
    [schedules],
  );

  return (
    <div className="space-y-10">
      {/* Toggle card */}
      <div className="bb-card p-6 md:p-7" data-testid="visits-pref-card">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-start gap-4 max-w-xl">
            <div className="w-10 h-10 rounded-full bg-[var(--bb-terracotta)]/10 text-[var(--bb-terracotta)] grid place-items-center shrink-0">
              <CalendarClock size={18}/>
            </div>
            <div>
              <div className="font-serif text-xl text-[var(--bb-forest)] leading-snug">{t("visits_toggle_label")}</div>
              <p className="text-xs text-[var(--bb-muted)] mt-1">{t("visits_toggle_hint")}</p>
            </div>
          </div>
          <Toggle checked={distPref} onChange={onToggle} testid="visits-toggle"/>
        </div>
      </div>

      {distPref && (
        <>
          <ScheduleForm
            t={t}
            supportedSchools={supportedSchools}
            onBooked={onBooked}
          />

          <div data-testid="visits-list">
            <h3 className="font-serif text-2xl text-[var(--bb-forest)] mb-4">{t("visits_list_title")}</h3>
            {upcoming.length === 0 ? (
              <div className="bb-card p-10 text-center text-sm text-[var(--bb-muted)]">{t("visits_list_empty")}</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {upcoming.map(s => (
                  <ScheduleCard key={s.schedule_id} s={s} t={t} onReschedule={() => onReschedule(s)} onCancel={() => onCancel(s.schedule_id)}/>
                ))}
              </div>
            )}
            {history.length > 0 && (
              <div className="mt-8">
                <div className="bb-overline text-[var(--bb-muted)] mb-3">HISTORY</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {history.map(s => (
                    <ScheduleCard key={s.schedule_id} s={s} t={t} historical/>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-[var(--bb-terracotta)]" : "bg-[var(--bb-border)]"}`}
      data-testid={testid}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`}
      />
    </button>
  );
}

function ScheduleForm({ t, supportedSchools, onBooked }) {
  const [schoolId, setSchoolId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const minDate = todayISO();
  const invalidDay = date && !isWeekdayISO(date);

  const submit = async (e) => {
    e.preventDefault();
    if (!schoolId || !date || !time) { toast.error(t("visits_toast_select")); return; }
    if (invalidDay) { toast.error("Sundays are not allowed."); return; }
    setSaving(true);
    try {
      await api.post("/donor/schedules", { school_id: schoolId, scheduled_date: date, scheduled_time: time, notes });
      setSchoolId(""); setDate(""); setTime(""); setNotes("");
      onBooked();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSaving(false); }
  };

  if (!supportedSchools || supportedSchools.length === 0) {
    return (
      <div className="bb-card p-8 text-sm text-[var(--bb-muted)]" data-testid="visits-no-schools">
        {t("visits_no_schools")}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bb-card p-6 md:p-8" data-testid="visits-form">
      <h3 className="font-serif text-2xl text-[var(--bb-forest)] mb-2">{t("visits_schedule_heading")}</h3>
      <p className="text-xs text-[var(--bb-muted)] mb-6">{t("visits_schedule_sub")}</p>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="bb-overline text-[var(--bb-muted)]">{t("visits_field_school")}</label>
          <select
            required
            value={schoolId}
            onChange={e => setSchoolId(e.target.value)}
            className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm"
            data-testid="visits-school"
          >
            <option value="">{t("visits_field_school_ph")}</option>
            {supportedSchools.map(s => (
              <option key={s.school_id} value={s.school_id}>{s.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="bb-overline text-[var(--bb-muted)]">{t("visits_field_date")}</label>
          <input
            type="date"
            required
            min={minDate}
            value={date}
            onChange={e => setDate(e.target.value)}
            className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm"
            data-testid="visits-date"
          />
          {invalidDay && <div className="text-[11px] text-[var(--bb-error)] mt-1">Sundays are not allowed. Please choose Mon–Sat.</div>}
        </div>

        <div>
          <label className="bb-overline text-[var(--bb-muted)]">{t("visits_field_time")}</label>
          <select
            required
            value={time}
            onChange={e => setTime(e.target.value)}
            className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm"
            data-testid="visits-time"
          >
            <option value="">{t("visits_field_time_ph")}</option>
            {TIME_SLOTS.map(ts => (
              <option key={ts} value={ts}>{formatTimeLabel(ts)}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="bb-overline text-[var(--bb-muted)]">{t("visits_field_notes")}</label>
          <input
            type="text"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={t("visits_field_notes_ph")}
            className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm"
            data-testid="visits-notes"
          />
        </div>
      </div>

      <div className="mt-7">
        <button
          type="submit"
          disabled={saving || invalidDay || !schoolId || !date || !time}
          className="bb-btn-primary inline-flex items-center gap-2"
          data-testid="visits-submit"
        >
          <CalendarDays size={14}/> {saving ? "…" : t("visits_btn_schedule")}
        </button>
      </div>
    </form>
  );
}

function ScheduleCard({ s, t, historical, onReschedule, onCancel }) {
  const statusKey = `visits_status_${s.status}`;
  const tagClass =
    s.status === "scheduled" || s.status === "rescheduled" ? "bb-tag-green" :
    s.status === "cancelled" ? "" : "";
  return (
    <div className="bb-card p-5" data-testid={`visit-${s.schedule_id}`}>
      <div className="flex items-center justify-between mb-3">
        <span className={`bb-tag inline-flex items-center gap-1 ${tagClass}`}>
          <CalendarClock size={11}/> {t(statusKey)}
        </span>
        <span className="text-[10px] text-[var(--bb-muted)]">{s.updated_at?.slice(0,10)}</span>
      </div>
      <div className="font-serif text-lg text-[var(--bb-forest)] leading-snug line-clamp-2 mb-3">{s.school_name}</div>
      <div className="grid grid-cols-2 gap-2 text-sm mb-2">
        <div className="flex items-center gap-2 text-[var(--bb-ink)]/85">
          <CalendarDays size={13} className="text-[var(--bb-muted)]"/>
          {s.scheduled_date}
        </div>
        <div className="flex items-center gap-2 text-[var(--bb-ink)]/85">
          <Clock size={13} className="text-[var(--bb-muted)]"/>
          {formatTimeLabel(s.scheduled_time)}
        </div>
      </div>
      {s.notes && (
        <div className="mt-2 text-xs text-[var(--bb-muted)] italic line-clamp-2 flex items-start gap-1">
          <FileEdit size={11} className="mt-[2px] shrink-0"/> {s.notes}
        </div>
      )}
      {!historical && (
        <div className="mt-4 flex items-center gap-3 text-xs">
          <button
            onClick={onReschedule}
            className="bb-link inline-flex items-center gap-1"
            data-testid={`visit-reschedule-${s.schedule_id}`}
          >
            <Edit3 size={12}/> {t("visits_btn_reschedule")}
          </button>
          <button
            onClick={onCancel}
            className="text-[var(--bb-error)] hover:underline inline-flex items-center gap-1"
            data-testid={`visit-cancel-${s.schedule_id}`}
          >
            <XCircle size={12}/> {t("visits_btn_cancel")}
          </button>
        </div>
      )}
    </div>
  );
}

function RescheduleModal({ target, t, onClose, onSaved }) {
  const [date, setDate] = useState(target.scheduled_date);
  const [time, setTime] = useState(target.scheduled_time);
  const [notes, setNotes] = useState(target.notes || "");
  const [saving, setSaving] = useState(false);

  const minDate = todayISO();
  const invalidDay = date && !isWeekdayISO(date);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) { toast.error(t("visits_toast_select")); return; }
    if (invalidDay) { toast.error("Sundays are not allowed."); return; }
    setSaving(true);
    try {
      await api.put(`/donor/schedules/${target.schedule_id}`, { scheduled_date: date, scheduled_time: time, notes });
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="reschedule-modal">
      <div className="bg-white rounded-md max-w-md w-full p-7 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--bb-muted)] hover:text-[var(--bb-ink)]" data-testid="reschedule-close">
          <X size={18}/>
        </button>
        <div className="bb-overline text-[var(--bb-terracotta)] mb-2">{target.school_name}</div>
        <h3 className="font-serif text-2xl text-[var(--bb-forest)] mb-2">{t("visits_reschedule_title")}</h3>
        <p className="text-xs text-[var(--bb-muted)] mb-5">{t("visits_reschedule_sub")}</p>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="bb-overline text-[var(--bb-muted)]">{t("visits_field_date")}</label>
            <input
              type="date"
              required
              min={minDate}
              value={date}
              onChange={e => setDate(e.target.value)}
              className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm"
              data-testid="reschedule-date"
            />
            {invalidDay && <div className="text-[11px] text-[var(--bb-error)] mt-1">Sundays are not allowed.</div>}
          </div>
          <div>
            <label className="bb-overline text-[var(--bb-muted)]">{t("visits_field_time")}</label>
            <select
              required
              value={time}
              onChange={e => setTime(e.target.value)}
              className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm"
              data-testid="reschedule-time"
            >
              {TIME_SLOTS.map(ts => (
                <option key={ts} value={ts}>{formatTimeLabel(ts)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="bb-overline text-[var(--bb-muted)]">{t("visits_field_notes")}</label>
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="mt-2 w-full bg-transparent border-b border-[var(--bb-border)] py-2 outline-none focus:border-[var(--bb-terracotta)] text-sm"
              data-testid="reschedule-notes"
            />
          </div>
          <div className="flex items-center gap-3 pt-1">
            <button type="submit" disabled={saving || invalidDay} className="bb-btn-primary text-sm" data-testid="reschedule-save">
              {saving ? "…" : t("visits_btn_save")}
            </button>
            <button type="button" onClick={onClose} className="text-sm text-[var(--bb-muted)] hover:text-[var(--bb-ink)]" data-testid="reschedule-cancel">
              {t("visits_btn_close")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ShareModal({ onClose, t }) {
  const msg = t("share_message");
  const waUrl = `https://wa.me/?text=${encodeURIComponent(msg)}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(msg); toast.success(t("share_copied")); }
    catch { toast.error("Copy failed"); }
  };

  const systemShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ text: msg }); }
      catch (err) {
        // User-cancelled share is AbortError — ignore silently; log anything else
        if (err?.name !== "AbortError") log.debug("[share] system share failed:", err?.message || err);
      }
    } else { copy(); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="share-modal">
      <div className="bg-white rounded-md max-w-md w-full p-7 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--bb-muted)] hover:text-[var(--bb-ink)]" data-testid="share-close">
          <X size={18}/>
        </button>
        <div className="bb-overline text-[var(--bb-terracotta)] mb-2">{t("share_title")}</div>
        <h3 className="font-serif text-2xl text-[var(--bb-forest)] mb-2">{t("share_impact")}</h3>
        <p className="text-xs text-[var(--bb-muted)] mb-5">{t("share_sub")}</p>
        <div className="bb-card p-4 text-sm text-[var(--bb-ink)]/85 whitespace-pre-line leading-relaxed mb-5 max-h-48 overflow-y-auto" data-testid="share-preview">
          {msg}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <a href={waUrl} target="_blank" rel="noreferrer" className="bb-btn-primary !py-3 text-xs inline-flex items-center justify-center gap-1" data-testid="share-whatsapp">
            <MessageCircle size={14}/> {t("share_whatsapp")}
          </a>
          <button onClick={systemShare} className="bb-btn-secondary !py-3 text-xs inline-flex items-center justify-center gap-1" data-testid="share-system">
            <Smartphone size={14}/> {t("share_system")}
          </button>
          <button onClick={copy} className="bb-btn-secondary !py-3 text-xs inline-flex items-center justify-center gap-1" data-testid="share-copy">
            <LinkIcon size={14}/> {t("share_copy")}
          </button>
        </div>
      </div>
    </div>
  );
}
